import { Button } from "@tql/ui-components-react/tql/buttons";
import { refreshCcw_01 } from "@tql/ui-components-react/untitled/icons";

import { StatusBanner } from "~/shared/components/status-banner/status-banner.tsx";

import type { BulkQuoteBatchStatus } from "../../schemas/bulk-quote-batch-status-schema.ts";

type BulkQuoteBatchStatusProps = Readonly<{
	status?: BulkQuoteBatchStatus["status"];
	processedCount?: number;
	requestCount?: number;
	errorCount?: number;
	isPollingTimedOut: boolean;
	onRetry: () => void;
	onDismiss: () => void;
}>;

const pendingStatuses = new Set<BulkQuoteBatchStatus["status"]>([
	"accepted",
	"processing",
]);

const getProgressMessage = (
	processedCount: number | undefined,
	requestCount: number | undefined,
): string | null =>
	processedCount == null || requestCount == null
		? null
		: `Processed ${processedCount} of ${requestCount} shipments.`;

const getCompletedMessage = (requestCount: number | undefined): string =>
	requestCount == null
		? "Your shipments finished processing and the Quote Board is up to date."
		: `All ${requestCount} shipments finished processing and the Quote Board is up to date.`;

const getCompletedWithErrorsMessage = (
	processedCount: number | undefined,
	requestCount: number | undefined,
	errorCount: number | undefined,
): string => {
	const progressMessage = getProgressMessage(processedCount, requestCount);
	const shipmentLabel = errorCount === 1 ? "shipment" : "shipments";
	const errorMessage =
		errorCount == null
			? "Some shipments could not be processed."
			: `${errorCount} ${shipmentLabel} could not be processed.`;

	return progressMessage == null
		? errorMessage
		: `${progressMessage} ${errorMessage}`;
};

export function BulkQuoteBatchStatus({
	status,
	processedCount,
	requestCount,
	errorCount,
	isPollingTimedOut,
	onRetry,
	onDismiss,
}: BulkQuoteBatchStatusProps) {
	if (isPollingTimedOut) {
		const progressMessage = getProgressMessage(processedCount, requestCount);

		return (
			<StatusBanner
				showLoader={false}
				title={"Bulk upload is taking longer than expected"}
			>
				<span>
					{progressMessage == null
						? "The latest processing status could not be retrieved. Automatic updates are paused."
						: `${progressMessage} Automatic updates are paused.`}
				</span>
				<Button onClick={onRetry} svgIcon={refreshCcw_01} variant={"secondary"}>
					Check again
				</Button>
				<Button onClick={onDismiss} variant={"tertiary"}>
					Dismiss
				</Button>
			</StatusBanner>
		);
	}

	if (status == null) {
		return null;
	}

	if (pendingStatuses.has(status)) {
		const message =
			status === "accepted"
				? "Preparing your shipments. They will appear here automatically."
				: (getProgressMessage(processedCount, requestCount) ??
					"Processing your shipments. They will appear here automatically.");

		return (
			<StatusBanner title={"Processing bulk upload"}>
				<span>{message}</span>
			</StatusBanner>
		);
	}

	if (status === "failed") {
		return (
			<StatusBanner showLoader={false} title={"Bulk upload failed"}>
				<span>
					None of the shipments could be processed. Check again, or start a new
					bulk upload.
				</span>
				<Button onClick={onRetry} svgIcon={refreshCcw_01} variant={"secondary"}>
					Check again
				</Button>
				<Button onClick={onDismiss} variant={"tertiary"}>
					Dismiss
				</Button>
			</StatusBanner>
		);
	}

	const isCompletedWithErrors = status === "completed_with_errors";

	return (
		<StatusBanner
			showLoader={false}
			title={
				isCompletedWithErrors
					? "Bulk upload completed with errors"
					: "Bulk upload complete"
			}
		>
			<span>
				{isCompletedWithErrors
					? getCompletedWithErrorsMessage(
							processedCount,
							requestCount,
							errorCount,
						)
					: getCompletedMessage(requestCount)}
			</span>
			<Button onClick={onDismiss} variant={"tertiary"}>
				Dismiss
			</Button>
		</StatusBanner>
	);
}
